import { useState, useEffect } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, SearchBar, Pagination, EmptyState, StatusBadge, useList, tableClass, theadClass, thClass, trClass, tdClass, tdFirstClass } from "@/components/shared";
import RightDrawer from "@/components/RightDrawer";
import { FormField } from "@/pages/Contacts";
import { useChannels } from "@/hooks/useChannels";
import { Webhook, Plus, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = {
  name: "", url: "", method: "POST", channel_id: "", event_types: [],
  content_type: "application/json", timeout_seconds: 30, verify_ssl: true,
  follow_redirects: false, secret_value: "", headers_text: "", is_active: true,
};

export default function Autopilots() {
  const channels = useChannels();
  const { data, pagination, loading, page, setPage, q, setQ, filters, setFilters, refetch } = useList("/autopilots");
  const [eventTypes, setEventTypes] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/meta/autopilot-event-types").then((r) => setEventTypes(r.data || [])); }, []);
  const channelName = (id) => channels.find((c) => c.id === id)?.name || "Semua channel";

  const openCreate = () => { setForm(EMPTY); setEditing(null); setDrawer(true); };
  const openEdit = (h) => {
    setEditing(h);
    setForm({
      ...EMPTY, ...h, channel_id: h.channel_id || "",
      headers_text: h.headers ? JSON.stringify(h.headers, null, 2) : "", secret_value: "",
    });
    setDrawer(true);
  };
  const toggleEvent = (e) => setForm((f) => ({ ...f, event_types: f.event_types.includes(e) ? f.event_types.filter((x) => x !== e) : [...f.event_types, e] }));

  const save = async () => {
    setSaving(true);
    try {
      let headers = {};
      if (form.headers_text.trim()) {
        try { headers = JSON.parse(form.headers_text); } catch { toast.error("Format headers harus JSON valid."); setSaving(false); return; }
      }
      const payload = {
        name: form.name, url: form.url, method: form.method,
        channel_id: form.channel_id || null, event_types: form.event_types,
        content_type: form.content_type, timeout_seconds: Number(form.timeout_seconds) || 30,
        verify_ssl: form.verify_ssl, follow_redirects: form.follow_redirects,
        headers, is_active: form.is_active,
      };
      if (form.secret_value) payload.secret_value = form.secret_value;
      if (editing) { await api.put(`/autopilots/${editing.id}`, payload); toast.success("Autopilot diperbarui."); }
      else { await api.post("/autopilots", payload); toast.success("Autopilot dibuat."); }
      setDrawer(false); refetch();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSaving(false); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Hapus autopilot "${h.name}"?`)) return;
    try { await api.del(`/autopilots/${h.id}`); toast.success("Autopilot dihapus."); refetch(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div>
      <PageHeader title="Autopilot" subtitle="Konfigurasi CURL / callback untuk integrasi manual Anda."
        action={<Button onClick={openCreate} className="bg-[#FFDA1A] hover:bg-[#F5CE00] text-[#111111]" data-testid="create-autopilot-button"><Plus className="w-4 h-4 mr-1.5" /> Autopilot Baru</Button>} />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Cari nama, URL..." testid="autopilot-search" />
        <Select value={filters.event_type || "all"} onValueChange={(v) => setFilters({ ...filters, event_type: v === "all" ? "" : v })}>
          <SelectTrigger className="w-52 bg-white" data-testid="autopilot-event-filter"><SelectValue placeholder="Event type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua event</SelectItem>{eventTypes.map((e) => <SelectItem key={e.value} value={e.value}>{e.value}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!loading && data.length === 0 ? (
        <EmptyState icon={Webhook} title="Belum ada autopilot" description="Buat konfigurasi callback / CURL untuk integrasi manual."
          action={<Button onClick={openCreate} className="bg-[#003399] hover:bg-[#002B80]"><Plus className="w-4 h-4 mr-1.5" /> Autopilot Baru</Button>} />
      ) : (
        <div className="bg-white rounded-lg overflow-hidden">
          <table className={tableClass}>
            <thead className={theadClass}>
              <tr>
                <th className={thClass}>Nama</th>
                <th className={`${thClass} hidden lg:table-cell`}>Method</th>
                <th className={`${thClass} hidden md:table-cell`}>Events</th>
                <th className={thClass}>Status</th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody>
              {data.map((h) => (
                <tr key={h.id} onClick={() => openEdit(h)} className={trClass} data-testid={`autopilot-row-${h.id}`}>
                  <td className={tdFirstClass}>
                    <p className="font-medium text-[#111111]">{h.name}</p>
                    <p className="text-xs text-[#767676] font-mono truncate max-w-[240px]">{h.url}</p>
                  </td>
                  <td className={`${tdClass} hidden lg:table-cell`}><span className="font-mono text-[11px] bg-[#00339912] text-[#003399] px-2 py-0.5 rounded-[4px] tracking-[0.05em]">{h.method}</span></td>
                  <td className={`${tdClass} hidden md:table-cell`}>
                    <div className="flex flex-wrap gap-1">{(h.event_types || []).map((e) => <span key={e} className="text-[10px] bg-[#00339912] text-[#003399] px-1.5 py-0.5 rounded-[3px] font-mono">{e}</span>)}</div>
                  </td>
                  <td className={tdClass}>
                    <StatusBadge status={h.is_active ? "active" : "inactive"} />
                  </td>
                  <td className={`${tdClass} text-right`}>
                    <button onClick={(e) => { e.stopPropagation(); remove(h); }} className="text-[#767676] hover:text-[#CC0008] p-1 transition-colors" data-testid={`delete-autopilot-${h.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination pagination={pagination} onPage={setPage} />

      <RightDrawer open={drawer} onClose={() => setDrawer(false)} title={editing ? "Edit Autopilot" : "Autopilot Baru"}
        description="Konfigurasi callback / CURL." testid="autopilot-drawer"
        footer={<>
          <Button variant="outline" onClick={() => setDrawer(false)}>Batal</Button>
          <Button onClick={save} disabled={saving || !form.name || !form.url || form.event_types.length === 0} className="bg-[#003399] hover:bg-[#002B80]" data-testid="autopilot-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
          </Button>
        </>}>
        <div className="space-y-4">
          <FormField label="Nama *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="ERP Production Hook" testid="autopilot-form-name" />
          <FormField label="URL Tujuan *" value={form.url} onChange={(v) => setForm({ ...form, url: v })} placeholder="https://..." testid="autopilot-form-url" />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="autopilot-form-method"><SelectValue /></SelectTrigger>
                <SelectContent>{["POST", "PUT", "PATCH"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <FormField label="Timeout (detik)" type="number" value={form.timeout_seconds} onChange={(v) => setForm({ ...form, timeout_seconds: v })} testid="autopilot-form-timeout" />
          </div>
          <div className="space-y-2">
            <Label>Channel (opsional)</Label>
            <Select value={form.channel_id || "none"} onValueChange={(v) => setForm({ ...form, channel_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="autopilot-form-channel"><SelectValue placeholder="Semua channel" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Semua channel</SelectItem>{channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Event Types *</Label>
            {eventTypes.map((e) => (
              <label key={e.value} className="flex items-center gap-2 text-sm p-2 rounded-[4px] bg-[#F5F5F5] hover:bg-[#ECECEC] cursor-pointer" data-testid={`event-${e.value}`}>
                <Checkbox checked={form.event_types.includes(e.value)} onCheckedChange={() => toggleEvent(e.value)} />
                <span className="font-mono text-xs">{e.value}</span>
              </label>
            ))}
          </div>
          <div className="space-y-2">
            <Label>Headers (JSON)</Label>
            <Textarea rows={3} value={form.headers_text} onChange={(e) => setForm({ ...form, headers_text: e.target.value })} placeholder='{"Authorization": "Bearer ..."}' className="font-mono text-xs" data-testid="autopilot-form-headers" />
          </div>
          <FormField label="Secret (opsional, disamarkan setelah simpan)" value={form.secret_value} onChange={(v) => setForm({ ...form, secret_value: v })} testid="autopilot-form-secret" />
          <div className="flex items-center justify-between p-3 rounded-[4px] bg-[#F5F5F5]">
            <Label>Verify SSL</Label>
            <Switch checked={form.verify_ssl} onCheckedChange={(v) => setForm({ ...form, verify_ssl: v })} data-testid="autopilot-form-ssl" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-[4px] bg-[#F5F5F5]">
            <Label>Follow Redirects</Label>
            <Switch checked={form.follow_redirects} onCheckedChange={(v) => setForm({ ...form, follow_redirects: v })} data-testid="autopilot-form-redirect" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-[4px] bg-[#F5F5F5]">
            <Label>Aktif</Label>
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="autopilot-form-active" />
          </div>
        </div>
      </RightDrawer>
    </div>
  );
}
